import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { AuthState } from "./auth";
import { DirectoryState } from "../types/directory";

// Define the initial state using that type
const initialState: DirectoryState = {
  entries: {
    data: [],
    waiting: false,
    loading: false,
    errors: [],
    options: {},
    pagination: {
      has_previous: false,
      has_next: false,
      current_page: 1,
      total_pages: 1,
      total_objects: 0
    },
  },
  entry: {
    data: null,
    waiting: false,
    loading: false,
    errors: [],
  },
  options: {
    data: {},
    loading: false,
    errors: []
  },
};

//////////////////////////////
//////// Async Thunks ////////
//////////////////////////////

// Get People
export const getDirectoryEntries = createAsyncThunk(
  "directory/entry/get",
  async (
    obj: {
      pageNumber: number;
      query: string | null;
      category: string | null;
      // topic: string | null;
      // interest: string | null;
      audience: string | null;
      postcode: string | null;
    },
    { getState, rejectWithValue }
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = auth.user === null ? {} : auth.access === null ? {} : {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        },
      };

      // Build endpoint URL
      const params: { [key: string]: string } = {};
      params.page = obj.pageNumber.toString();
      if (obj.query !== null && obj.query !== "") params.query = obj.query;
      if (obj.category !== null && obj.category !== "") params.category = obj.category;
      if (obj.audience !== null && obj.audience !== "") params.audience = obj.audience;
      if (obj.postcode !== null && obj.postcode !== "") params.postcode = obj.postcode;
      const searchParams = new URLSearchParams(params).toString();
      const url = `/api/directory/entry${searchParams === "" ? "" : "?" + searchParams}`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

// Get People by Slug
// export const getDirectoryEntryBySlug = createAsyncThunk(
//   "directory/people/slug/get",
//   async (
//     obj: {
//       slug: string | undefined;
//     },
//     { getState, rejectWithValue }
//   ) => {
//     try {
//       // Build endpoint URL
//       const url = `/api/directory/people/${obj.slug}`;

//       // Send request
//       const res = await axios.get(url);
//       return { data: res.data, status: res.status };
//     } catch (err: any) {
//       if (err.response && err.response.data.message) {
//         return rejectWithValue(err.response.data.message);
//       } else {
//         return rejectWithValue(err.message);
//       }
//     }
//   }
// );

// Get Directory people Locations
export const getDirectoryEntryLocations = createAsyncThunk(
  "directory/entry/locations/get",
  async (
    args,
    { getState, rejectWithValue }
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = auth.user === null ? {} : auth.access === null ? {} : {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        },
      };

      // Build endpoint URL
      const url = `/api/directory/entry/city`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

/////////////////////////////
//////// Redux slice ////////
/////////////////////////////

const directorySlice = createSlice({
  name: "directory",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder

      // Get People
      .addCase(getDirectoryEntries.pending, (state, action) => {
        const pageNumber = action.meta.arg.pageNumber;
        if (pageNumber === 1) {
          state.entries.loading = true;
        } else {
          state.entries.waiting = true;
        }
      })
      .addCase(getDirectoryEntries.fulfilled, (state, action) => {
        const pageNumber = action.meta.arg.pageNumber;
        if (pageNumber === 1) {
          state.entries.loading = false;
        } else {
          state.entries.waiting = false;
        }
        state.entries.data = pageNumber === 1 ? action.payload.data.data : [...state.entries.data, ...action.payload.data.data];
        state.entries.options = action.payload.data.options;
        state.entries.pagination = action.payload.data.pagination;
      })
      .addCase(getDirectoryEntries.rejected, (state, action) => {
        state.entries.waiting = false;
        state.entries.loading = false;
        state.entries.errors.push(action.payload as string);
      })

      // Get People by Slug
      // .addCase(getDirectoryPeopleBySlug.pending, (state) => {
      //   state.entry.waiting = true;
      //   state.entry.loading = true;
      // })
      // .addCase(getDirectoryPeopleBySlug.fulfilled, (state, action) => {
      //   state.entry.waiting = false;
      //   state.entry.loading = false;
      //   state.entry.data = action.payload.data;
      // })
      // .addCase(getDirectoryPeopleBySlug.rejected, (state, action) => {
      //   state.entry.waiting = false;
      //   state.entry.loading = false;
      //   state.entry.errors.push(action.payload as string);
      // })

      // Get Directory people Locations
      .addCase(getDirectoryEntryLocations.pending, (state) => {
        state.options.loading = true;
      })
      .addCase(getDirectoryEntryLocations.fulfilled, (state, action) => {
        state.options.loading = false;
        state.options.data.cities = action.payload.data;
      })
      .addCase(getDirectoryEntryLocations.rejected, (state, action) => {
        state.options.loading = false;
        state.options.errors.push(action.payload as string);
      });
  },
});

export default directorySlice.reducer;
